import type z from 'zod'
import i18next from 'i18next'
import { type Reactive, watch, reactive, effectScope, onScopeDispose } from 'vue'
import type { DeleteResult, UpdateResult, InsertResult, CompiledQuery } from 'kysely'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import * as fastq from 'fastq'
import merge from 'deepmerge'
import { isPartialObject } from '@/lib/utils/functional'
import { foreignKeyOf, reverseForeignKeyOf } from '@/lib/relation-graph'
import type { DB } from '@/lib/kysely-d'
import { useDataTimestamp } from '@/composables/useDataTimestamp'

type Fields = Record<string, any>
type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>
    }
  : T

export type InsertQuery = CompiledQuery<InsertResult>
export type UpdateQuery = CompiledQuery<UpdateResult>
export type DeleteQuery = CompiledQuery<DeleteResult>
export type DMLQuery = InsertQuery | UpdateQuery | DeleteQuery

export const errorParentIsRemoved = {
  get message() {
    return i18next.t('ui.error.parentIsRemoved')
  },
  params: {
    customCode: 'invalid_relation'
  }
}

export const errorContainsInvalidItems = {
  get message() {
    return i18next.t('ui.projectEdit.invalidItemIncluded')
  },
  params: {
    customCode: 'invalid_relation'
  }
}

const makeSingleValidateQueueFunction = () => {
  const q = fastq.promise(async (fn) => fn(), 1)
  return {
    queue: q,
    func: (fn: () => Promise<void>) => {
      if (q.length() > 0) q.kill()
      q.push(fn)
    }
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Stageable is a interface that can add to the Stage.
 * @property base - Reactive entity from live DB data.
 * @property modified - modified value in form.
 * @property context - contextual, "should not be modified" values.
 * @method reset() - reset all modified fields.
 * @method mutateLocalDB(trx) - mutate local database
 * @method asMutation() - returns GraphQL query.
 * @method isValidAsync() - returns is valid or not.
 * @method isModified() - returns if modified or not.
 * @method isNewEntity() - returns if modified or not.
 * @method errors() - returns error object.
 */
export interface Stageable {
  base: Fields | undefined
  modified: DeepPartial<Fields>
  context: DeepPartial<Fields>
  errors: Promise<z.ZodError<any> | undefined>
  parsed: Promise<z.output<z.ZodType<any, any, any>> | undefined>
  reset(): void
  mutateLocalDB(): Promise<DMLQuery[]>
  asMutation(commitId: string): Promise<MutationOperationAndVariables[]>
  isValidAsync(): Promise<boolean>
  isModified(): boolean
  isNewEntity(): boolean
  delete(): void
  merged: Record<string, any>
  table: keyof DB
  isDeleted: boolean
  readonly className: string
}

export abstract class Mutator<T extends z.ZodType<any, any, any>> implements Stageable {
  base: z.input<T> | undefined = undefined
  modified: DeepPartial<z.input<T>>
  context: DeepPartial<z.input<T>>
  abstract table: keyof DB
  abstract schema: z.ZodSchema
  errors: Promise<z.ZodError<T> | undefined>
  parsed: Promise<z.output<T> | undefined>
  private deleted = false
  singleValidateQueueFunction: (fn: () => Promise<void>) => void
  queue = fastq.promise(async (fn) => fn(), 1)
  readonly className: string
  // Hack to guard instanciate Mutator with 'new' expression.
  private static __sentinel = Symbol('Can not instaceate with new')
  /**
   * @protected indirectTables - 間接的に参照しているテーブル名の配列。
   * この配列に含まれるテーブルのデータが変更された場合にも、関連チェックを行う。
   * 例えば、`project.custom_fields` のようなJSONBカラムにて格納している場合などに使用する。
   */
  protected indirectTables?: Array<keyof DB> = []

  constructor(_: typeof Mutator.__sentinel) {
    this.base = undefined
    this.modified = {} as DeepPartial<z.input<T>>
    this.context = {} as DeepPartial<z.input<T>>
    this.errors = Promise.resolve(undefined)
    this.parsed = Promise.resolve(undefined)

    const { queue, func } = makeSingleValidateQueueFunction()

    this.queue = queue
    this.singleValidateQueueFunction = func

    // minify しても壊れないよう、各派生クラスで静的にクラス名を定義させる
    const ctor = this.constructor as typeof Mutator & { stageableClassName?: string }
    if (ctor.stageableClassName == null) {
      throw new Error('Mutator subclass must define static stageableClassName = "YourClassName"')
    }
    this.className = ctor.stageableClassName
  }

  static createInstance<T extends Mutator<z.ZodType<any, any, any>>>(
    this: new (sym: typeof Mutator.__sentinel) => T
  ): Reactive<T> {
    const self = reactive(new this(Mutator.__sentinel))
    // Vue 3.2 以降、`effectScope` を使うことで、コンポーネントスコープに依存しない watcher を作成できる。
    // https://ja.vuejs.org/api/reactivity-advanced#effectscope
    // `detached` フラグを `true` にすれば、現在アクティブなコンポーネントスコープとは切り離された“孤立スコープ” が得られる。
    // このスコープ内で生成した `watch` は、スコープを `stop()` しない限り生き続ける。
    // ① コンポーネントと無関係なスコープを作成
    const scope = effectScope(true) // detached = true
    scope.run(() => {
      // ② ここで作られた watch は scope.stop() まで存続
      watch(
        () => self.modified,
        () => self.validate(),
        { deep: true }
      )
      const lifetime = new Promise<void>((resolve) => onScopeDispose(resolve))
      self.startRelationChecker(lifetime)
    })
    return self
  }

  private relatedTables() {
    // 直接参照しているテーブル
    const refTables = new Set(foreignKeyOf(this.table).map((r) => r.to_table))
    // M2Mを介して参照しているテーブル
    const m2mTables = new Set(
      reverseForeignKeyOf(this.table).flatMap((r) => {
        if (!r.from_table.startsWith(this.table + '_to')) return []
        return foreignKeyOf(r.from_table)
          .filter((rr) => rr.to_table !== this.table)
          .map((rr) => rr.to_table)
      })
    )
    return [...refTables, ...m2mTables, ...(this.indirectTables || [])]
  }

  async startRelationChecker(lifetime: Promise<void>): Promise<void> {
    console.debug('related tables', this.relatedTables())
    const { timestampRefsOn } = useDataTimestamp()
    watch(timestampRefsOn(lifetime, this.relatedTables()), () => {
      if (this.isModified()) this.validate()
    })
  }

  abstract insertLocalDB(): Promise<DMLQuery[]>
  abstract updateLocalDB(): Promise<DMLQuery[]>
  abstract deleteLocalDB(): Promise<DMLQuery[]>
  async mutateLocalDB(): Promise<DMLQuery[]> {
    if (this.deleted) return this.deleteLocalDB()
    else if (this.isNewEntity()) return this.insertLocalDB()
    else return this.updateLocalDB()
  }

  abstract asUpdateMutation(commitId: string): Promise<MutationOperationAndVariables[]>
  abstract asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]>
  abstract asInsertMutation(commitId: string): Promise<MutationOperationAndVariables[]>
  async asMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    await this.queue.drained()
    if (this.deleted) return this.asDeleteMutation(commitId)
    else if (this.isNewEntity()) return this.asInsertMutation(commitId)
    else return this.asUpdateMutation(commitId)
  }

  get merged(): DeepPartial<z.input<T>> {
    const mergeAll = merge.all([this.base ?? {}, this.modified, this.context], {
      arrayMerge: (destinationArray, sourceArray) => sourceArray
    })
    return mergeAll as DeepPartial<z.input<T>>
  }

  reset() {
    for (const key in this.modified) {
      delete this.modified[key as keyof typeof this.modified]
    }
    this.deleted = false
  }

  validate() {
    this.singleValidateQueueFunction(async () => {
      const result = await this.schema.safeParseAsync(this.merged)
      this.errors = Promise.resolve(result.success ? undefined : result.error)
      this.parsed = result.success ? result.data : undefined
      console.debug('validation complete.', this.parsed)
    })
  }

  isModified(): boolean {
    if (this.isNewEntity()) return Object.keys(this.modified).length > 0
    return !isPartialObject(this.modified, this.base as z.input<T>)
  }

  isNewEntity(): boolean {
    return this.base === undefined
  }

  delete(): void {
    if (this.isNewEntity()) {
      this.reset()
    } else {
      this.deleted = true
    }
  }

  get isDeleted(): boolean {
    return this.deleted
  }

  async isValidAsync(): Promise<boolean> {
    if (this.deleted) return true
    return (await this.errors) === undefined && (await this.parsed) !== undefined
  }

  async waitUntilQueueIsEmpty(): Promise<void> {
    await sleep(0) // イベントループを進めてfastqのキュー処理を開始させるため
    await this.queue.drained()
  }
}
